// Implements: F-VER-007, F-UIX-021, F-UIX-009, F-UIX-010, F-UIX-013
/**
 * Compare this version with another one of the same mockup.
 *
 * The versions to choose from are the tree's, in the order they were made and
 * without the one the page is on. Choosing one asks for the comparison (a read,
 * free of charge), with the CHOSEN version as the base and THIS one as the
 * target: the heading says "from X to Y" so nobody has to guess which way the
 * signs point. Going back to the prompt clears it.
 */

import { useState } from "react";
import type { ReactElement } from "react";

import { useTranslation } from "../../../shared/i18n";
import { Field } from "../../../shared/ui/molecules/Field";
import { ErrorState } from "../../../shared/ui/molecules/ErrorState";
import { LoadingState } from "../../../shared/ui/molecules/LoadingState";
import { VersionDiff } from "../components/VersionDiff";
import { useVersionDiff } from "../hooks/useVersionDiff";
import { useVersionTree } from "../hooks/useVersionTree";
import styles from "./VersionCompareContainer.module.css";

export const COMPARE_FIELD_ID = "compare-with";

export interface VersionCompareContainerProps {
  readonly mockupId: string;
  readonly versionId: string;
}

export function VersionCompareContainer({
  mockupId,
  versionId,
}: VersionCompareContainerProps): ReactElement | null {
  const { t } = useTranslation();
  const tree = useVersionTree(mockupId);
  const [chosen, setChosen] = useState<string | null>(null);
  const diff = useVersionDiff(mockupId, chosen, versionId);

  if (tree.status === "pending") return <LoadingState label={t("common.state.loading")} />;
  if (tree.status === "error") {
    return (
      <ErrorState
        error={tree.error}
        onRetry={() => {
          tree.refetch();
        }}
      />
    );
  }

  const nodes = tree.data.nodes;
  const others = nodes
    .filter((node) => node.id !== versionId)
    .sort((left, right) => left.sequence_number - right.sequence_number);
  if (others.length === 0) return <p className={styles.none}>{t("compare.none")}</p>;

  const numberOf = (id: string): number =>
    nodes.find((node) => node.id === id)?.sequence_number ?? 0;

  return (
    <section className={styles.compare} aria-labelledby="compare-title">
      <h2 id="compare-title" className={styles.title}>
        {t("compare.title")}
      </h2>
      <Field id={COMPARE_FIELD_ID} label={t("compare.pick.label")}>
        <select
          id={COMPARE_FIELD_ID}
          className={styles.select}
          value={chosen ?? ""}
          onChange={(event) => {
            setChosen(event.target.value === "" ? null : event.target.value);
          }}
        >
          <option value="">{t("compare.pick.none")}</option>
          {others.map((node) => (
            <option key={node.id} value={node.id}>
              {t("versions.node", { n: node.sequence_number })}
            </option>
          ))}
        </select>
      </Field>

      {chosen !== null && diff.isPending ? (
        <LoadingState label={t("common.state.loading")} />
      ) : null}
      {chosen !== null && diff.isError ? (
        <ErrorState
          error={diff.error}
          onRetry={() => {
            void diff.refetch();
          }}
        />
      ) : null}
      {chosen !== null && diff.isSuccess ? (
        <>
          <h3 className={styles.heading}>
            {t("compare.heading", { from: numberOf(chosen), to: numberOf(versionId) })}
          </h3>
          <VersionDiff pieces={diff.data.per_kind} />
        </>
      ) : null}
    </section>
  );
}
